import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Card, Stack } from '@mui/material';
import { useLocation } from 'react-router';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';

import { Header } from '@app/sections/header';
import { Strings } from '@app/constants';
import { ConfirmationDialog, DeleteDialog, FormProvider, Scrollbar } from '@app/components';
import { LeaveInfo } from './leave-info';
import { LeaveStatusCard } from '@app/components/card';
import { getLeaveDetails, getLeaves, updateLeaveStatus } from '../slice';
import {
  selectLeaveDetailsState,
  selectLeaveState,
  selectUpdateLeaveStatusState,
} from '../selectors';
import { useDialog, useRequestState } from '@app/hooks';
import { getLeaveManagement, selectLeaveManagementState } from '@app/modules/leave-setting';
import { updateLeaveStatusSchema } from '../validator';
import { useAuthRole } from '@app/utils';

const LeaveDetailsPage = () => {
  const { state } = useLocation();
  const dispatch = useDispatch();
  const leaveId = state?.id;
  const row = state.row;
  const update = state.update;
  const itSelf = state.itSelf;
  const { isEmployee, isIntern } = useAuthRole();
  const { data } = useRequestState({
    stateSelector: selectLeaveDetailsState,
    errorShown: true,
  });

  const goBack = () => {
    history.back();
  };

  useEffect(() => {
    if (leaveId) {
      dispatch(getLeaveDetails({ id: leaveId }));
    }
  }, [leaveId, dispatch]);

  const leaves = useSelector(selectLeaveState);
  const totalLeaves = useSelector(selectLeaveManagementState);

  useEffect(() => {
    dispatch(getLeaves(itSelf));
  }, [dispatch, itSelf]);

  const userApprovedLeaves = leaves?.data?.filter(
    (leave) =>
      leave.update === 'Approved' &&
      leave.user.firstName === (data?.user.firstName ?? '') &&
      leave.user.lastName === (data?.user.lastName ?? '')
  );
  let approvedLeaves = 0;

  const leaveTypeCounts: Record<string, number> = {};

  totalLeaves?.data
    ?.filter((leaveType) => leaveType.status)
    ?.forEach((leaveType) => {
      leaveTypeCounts[leaveType.leaveType.toLowerCase()] = 0;
    });

  userApprovedLeaves?.forEach((leave) => {
    const leaveType = leave.leaveType.toLowerCase();
    leave.periods.forEach((period: any) => {
      if (period.period === 'Full Day') {
        approvedLeaves += 1;
        leaveTypeCounts[leaveType] = (leaveTypeCounts[leaveType] || 0) + 1;
      } else if (period.period.includes('Half')) {
        approvedLeaves += 0.5;
        leaveTypeCounts[leaveType] = (leaveTypeCounts[leaveType] || 0) + 0.5;
      }
    });
  });

  const leaveTypeCards = Object.entries(leaveTypeCounts).map(([leaveType, days]) => ({
    title: leaveType,
    days,
  }));

  useEffect(() => {
    dispatch(getLeaveManagement());
  }, [dispatch]);
  const leaveState = useSelector(selectLeaveManagementState);

  const totalAllowedLeaves =
    leaveState.data
      ?.filter((leaveType) => leaveType.status)
      .reduce((acc, leaveType) => acc + Number(leaveType.allowedDays), 0) ?? 0;

  const LossOfPayLeaves = Math.max(approvedLeaves - totalAllowedLeaves, 0);

  const methods = useForm({
    resolver: yupResolver(updateLeaveStatusSchema) as any,
  });
  const [selectedStatus, setSelectedStatus] = useState('Pending');

  useEffect(() => {
    if (data?.update) {
      setSelectedStatus(data.update);
    }
  }, [data?.update]);

  const handleDropDownOption = (option: string) => {
    setSelectedStatus(option);
    option === 'Approved' ? deleteDialog.show() : confirmDialog.show();
  };

  const onPressConfirm = (reason?: string) => {
    dispatch(updateLeaveStatus({ ...row, req: selectedStatus, reason: reason ?? '', id: leaveId }));
  };

  const deleteDialog = useDialog();
  const confirmDialog = useDialog();

  const { loading } = useRequestState({
    stateSelector: selectUpdateLeaveStatusState,
    onSuccess: deleteDialog.hide,
    successMessageShown: true,
    errorShown: true,
  });
  const { loading: forApproved } = useRequestState({
    stateSelector: selectUpdateLeaveStatusState,
    onSuccess: confirmDialog.hide,
    successMessageShown: true,
    errorShown: true,
  });
  const isDropDownDisabled =
    selectedStatus === Strings.status.approved ||
    selectedStatus === Strings.status.declined ||
    isEmployee ||
    isIntern ||
    itSelf;

  return (
    <>
      <Box sx={{ mr: 3 }}>
        <Header
          goBack={goBack}
          title={Strings.page_title.details}
          helperText={''}
          dropDownOptions={
            update === Strings.status.draft
              ? undefined
              : [Strings.status.pending, Strings.status.approved, Strings.status.declined]
          }
          dropDownLabel={update === Strings.status.draft ? undefined : Strings.leave.updateStatus}
          dropDownSelect={update === Strings.status.draft ? undefined : selectedStatus}
          onClickDropDownOption={update === Strings.status.draft ? undefined : handleDropDownOption}
          dropDownDisabled={isDropDownDisabled}
        />
      </Box>
      <FormProvider methods={methods}>
        <ConfirmationDialog
          title={Strings.leave.declinedLeave}
          confirmationButtonTitle={Strings.button.save}
          caption={Strings.leave.declinedCaption}
          loading={forApproved}
          visible={confirmDialog.visible}
          onClickConfirm={onPressConfirm}
          onClose={confirmDialog.hide}
        />
      </FormProvider>
      <DeleteDialog
        title={`${Strings.common.update} ${Strings.field.leave}`}
        deleteButtonTitle={Strings.button.confirm}
        caption={Strings.common.updateCaption(Strings.field.request)}
        loading={loading}
        visible={deleteDialog.visible}
        onClickDelete={() => {
          onPressConfirm('');
        }}
        onClose={deleteDialog.hide}
      />
      <Scrollbar>
        <LeaveInfo leaveData={data} />
        <Card sx={{ m: 1.5 }}>
          <Stack p={2}>
            <Stack
              direction={'row'}
              mt={2}
              mb={2}
              flexWrap={'wrap'}
              gap={2}
              alignItems={'center'}
              justifyContent={'center'}
            >
              <LeaveStatusCard
                title={Strings.leave.totalAllowedLeaves}
                iconImg={'ic_totalLeaves'}
                days={totalAllowedLeaves}
                totalAllowedLeaves={totalAllowedLeaves}
              />
              <LeaveStatusCard
                title={Strings.leave.takenLeaves}
                iconImg={'ic_totalLeaves'}
                days={approvedLeaves}
                totalAllowedLeaves={totalAllowedLeaves}
              />
              <LeaveStatusCard
                title={Strings.leave.lossOfPay}
                days={LossOfPayLeaves}
                iconImg={'ic_lossOfPay'}
                LossOfPayLeaves
              />
            </Stack>
            <Stack
              direction={'row'}
              mt={2}
              mb={2}
              flexWrap={'wrap'}
              gap={2}
              alignItems={'center'}
              justifyContent={'center'}
            >
              {leaveTypeCards.map((card, index) => (
                <LeaveStatusCard
                  key={index}
                  title={card.title}
                  days={card.days}
                  iconImg={'ic_pending'}
                />
              ))}
            </Stack>
          </Stack>
        </Card>
      </Scrollbar>
    </>
  );
};
export { LeaveDetailsPage };
